import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';

const CONSENT_KEY = 'techenabler-consent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// Banner de consentimento (LGPD) + Consent Mode v2.
//
// O snippet do GTM já está sempre presente no <head> (ver index.html), com o
// estado default de analytics_storage declarado inline ali, lido do mesmo
// localStorage que este componente escreve. O que este banner faz é só
// atualizar o consentimento quando o usuário escolhe, nunca injetar a tag.
export default function CookieConsentBanner() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const escolha = localStorage.getItem(CONSENT_KEY);
      if (escolha !== 'granted' && escolha !== 'denied') {
        setVisible(true);
      }
    } catch (e) {
      // localStorage bloqueado: sem persistência não dá pra lembrar a
      // escolha, mas ainda dá pra perguntar nesta navegação.
      setVisible(true);
    }
  }, []);

  function registrar(aceitou: boolean) {
    try {
      localStorage.setItem(CONSENT_KEY, aceitou ? 'granted' : 'denied');
    } catch (e) {
      // Segue sem persistir: a escolha vale só para esta navegação.
      console.warn('consent: não foi possível gravar a escolha —', e);
    }

    window.dataLayer = window.dataLayer || [];
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: aceitou ? 'granted' : 'denied',
      });
    }
    window.dataLayer.push({ event: aceitou ? 'consent_aceito' : 'consent_recusado' });

    setVisible(false);
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          role="dialog"
          aria-live="polite"
          aria-label={t('Consentimento de cookies', 'Consentimiento de cookies', 'Cookie consent')}
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg"
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <p className="text-sm text-muted-foreground">
              {t(
                'Usamos cookies para melhorar sua experiência e analisar o uso do site. ',
                'Usamos cookies para mejorar tu experiencia y analizar el uso del sitio. ',
                'We use cookies to improve your experience and analyze site usage. '
              )}
              <Link to="/privacidade" className="underline underline-offset-2 hover:text-foreground">
                {t('Saiba mais na nossa política de privacidade', 'Conoce más en nuestra política de privacidad', 'Learn more in our privacy policy')}
              </Link>
              .
            </p>
            <div className="flex shrink-0 items-center gap-3">
              <Button variant="outline" size="default" onClick={() => registrar(false)}>
                {t('Recusar', 'Rechazar', 'Decline')}
              </Button>
              <Button variant="default" size="default" onClick={() => registrar(true)}>
                {t('Aceitar', 'Aceptar', 'Accept')}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
